// API client for the Assessment question bank (/api/assessment/admin/questions).
//
// Backend contract (sia-backend):
//   - every response is { data, isError, statusCode, errorMessage }
//   - GET list returns a page in `data`: { content, page, size, totalElements,
//     totalPages, hasNext }
//   - ids are integers; a question stores a single topicId (+ optional
//     subTopicId) while chapter/subject/course are many-to-many above it, so
//     list rows carry `chapterIds/Names`, `subjectIds/Names`, `courseIds/Names`
//   - DELETE is a soft delete (status -> ARCHIVED)
//   - status is changed with PATCH /{id}/status or PATCH /bulk-status
import api from "../axiosInstance";

const BASE = "/api/assessment/admin/questions";

export const QUESTION_TYPES = [
  { value: "MCQ", label: "MCQ" },
  { value: "TRUE_FALSE", label: "True/False" },
  { value: "DESCRIPTIVE", label: "Descriptive" },
  { value: "FILL_IN_THE_BLANK", label: "Fill in the Blank" },
];

export const DIFFICULTIES = [
  { value: "EASY", label: "Easy" },
  { value: "MODERATE", label: "Moderate" },
  { value: "DIFFICULT", label: "Difficult" },
  { value: "VERY_DIFFICULT", label: "Very Difficult" },
];

export const QUESTION_STATUSES = [
  { value: "DRAFT", label: "Draft" },
  { value: "PUBLISHED", label: "Published" },
  { value: "ARCHIVED", label: "Archived" },
];

export const QUESTION_PURPOSES = [
  { value: "PRACTICE_EXAMPLE", label: "Practice Example" },
  { value: "EXERCISE", label: "Exercise" },
  { value: "ASSESSMENT", label: "Assessment" },
];

export const QUESTION_USAGES = [
  { value: "NEW", label: "New" },
  { value: "PYQ", label: "PYQ" },
];

export const FINAL_OUTPUTS = [
  { value: "BOOK_PURPOSE", label: "Book" },
  { value: "APP_PURPOSE", label: "App" },
  { value: "BOTH", label: "Both" },
];

export const labelOf = (options, value) =>
  options.find((o) => o.value === value)?.label ?? value ?? "—";

// Stored image paths look like "/uploads/assessments/questions/images/x.png".
export const questionMediaUrl = (path) =>
  !path ? "" : /^https?:\/\//.test(path) ? path : `https://siaedutech.com/public/${path.replace(/^\//, "")}`;

// Drop undefined/empty-string params so they aren't sent as `?search=`.
const cleanParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""),
  );

const unwrap = (res) => res.data?.data;

// filters: courseId, subjectId, chapterId, topicId, subTopicId, difficulty,
// status, questionType, questionUsage, finalOutput, questionPurpose, search, page, size.
export const listQuestions = async ({ page = 0, size = 20, ...filters } = {}) => {
  const res = await api.get(BASE, { params: cleanParams({ ...filters, page, size }) });
  return unwrap(res) || { content: [], page, size, totalElements: 0, totalPages: 0, hasNext: false };
};

// New questions are always created as DRAFT; use updateQuestionStatus to publish.
export const createQuestion = (payload) => api.post(BASE, payload).then(unwrap);
export const updateQuestion = (id, payload) => api.put(`${BASE}/${id}`, payload).then(unwrap);

// Soft delete: resolves with { id, status: "ARCHIVED", message }.
export const archiveQuestion = (id) => api.delete(`${BASE}/${id}`).then(unwrap);

export const updateQuestionStatus = (id, status) =>
  api.patch(`${BASE}/${id}/status`, { status }).then(unwrap);

// Resolves with { updatedCount, errors: [{ id, errorMessage }], message }.
export const bulkUpdateQuestionStatus = (ids, status) =>
  api.patch(`${BASE}/bulk-status`, { ids, status }).then(unwrap);

// Word (.docx) upload. Resolves with { createdCount, createdQuestionIds,
// errors: [{ question, errorMessage }] } — HTTP 200 even when rows failed.
// bookPartType ("SYNOPSIS" | "QUESTION_BANK") makes the backend reject a file of the other kind.
export const bulkUploadQuestions = (file, bookPartType) => {
  const formData = new FormData();
  formData.append("file", file);
  if (bookPartType) formData.append("bookPartType", bookPartType);
  return api.post(`${BASE}/bulk-upload`, formData).then(unwrap);
};

// Indexes (or re-indexes) a chapter's synopsis for RAG. The backend answers 202 and runs the job async.
export const indexAssessmentChapter = (chapterId) =>
  api.post(`/api/v1/rag/index/assessment/chapter/${chapterId}`).then(unwrap);

// Job status: PENDING | RUNNING | SUCCEEDED | FAILED (errorMessage is set on FAILED).
export const getIndexJob = (jobId) => api.get(`/api/v1/rag/index/job/${jobId}`).then(unwrap);

// Uploads one image (question image or any option image) and resolves with the
// stored path to put in questionImagePath / optionXImagePath.
export const uploadQuestionImage = (file) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post("/api/ftp/uploadAssessmentQuestionImage", formData).then((res) => unwrap(res)?.path);
};
